import React, { createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RoleLoginPage from "./pages/RoleLoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminDashboard from "./pages/AdminDashboard";
import StaffDashboard from "./pages/StaffDashboard";
import ClientDashboard from "./pages/ClientDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import BookingForm from "./pages/BookingForm";
import BookingDetail from "./pages/BookingDetail";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import SettingsPage from "./pages/SettingsPage";
import InvoicesPage from "./pages/InvoicesPage";
import TrackingPage from "./pages/TrackingPage";
import ReportsPage from "./pages/ReportsPage";
import RatingsPage from "./pages/RatingsPage";
import GuestBookingPage from "./pages/GuestBookingPage";
import ExtrasPage from "./pages/ExtrasPage";
import BulkBookingPage from "./pages/BulkBookingPage";
import CorporatePortal from "./pages/CorporatePortal";
import QuoteAcceptPage from "./pages/QuoteAcceptPage";
import LiveTrackingPage from "./pages/LiveTrackingPage";
import RatingPage from "./pages/RatingPage";
import PayrollPage from "./pages/PayrollPage";
import RatingsManagementPage from "./pages/RatingsManagementPage";

// City Pages
import BrisbanePage from "./pages/cities/BrisbanePage";
import GoldCoastPage from "./pages/cities/GoldCoastPage";
import SydneyPage from "./pages/cities/SydneyPage";
import MelbournePage from "./pages/cities/MelbournePage";
import CairnsPage from "./pages/cities/CairnsPage";
import PerthPage from "./pages/cities/PerthPage";
import CitiesPage from "./pages/CitiesPage";
import CorporateTravelPage from "./pages/CorporateTravelPage";

// Service Pages
import FleetPage from "./pages/FleetPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import FAQPage from "./pages/FAQPage";
import TermsPage from "./pages/TermsPage";
import AirportTransfersPage from "./pages/services/AirportTransfersPage";
import MinibusCharterPage from "./pages/services/MinibusCharterPage";
import HensBucksPage from "./pages/services/HensBucksPage";
import LocationPage from "./pages/LocationPage";
import ToursPage from "./pages/ToursPage";
import TourPackagePage from "./pages/TourPackagePage";
import WeddingsPage from "./pages/services/WeddingsPage";
import CruiseTerminalPage from "./pages/services/CruiseTerminalPage";
import SpecialEventsPage from "./pages/services/SpecialEventsPage";

// Animation
import { AnimatePresence } from "framer-motion";
import PageTransition from "./components/PageTransition";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" }
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  // Initialize user from localStorage synchronously to avoid flash
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");
    if (storedUser && storedToken) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(false);

  const login = async (email, password, role = null) => {
    try {
      const payload = { email, password };
      if (role) payload.role = role;
      
      const response = await api.post("/auth/login", payload);
      
      // Check if role selection is required
      if (response.data.requires_role_selection) {
        return { 
          success: false, 
          requiresRoleSelection: true, 
          availableRoles: response.data.available_roles 
        };
      }
      
      const { access_token, user } = response.data;
      
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
      
      toast.success("Login successful!");
      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      const { access_token, user } = response.data;
      
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);
      
      toast.success("Registration successful!");
      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logged out successfully");
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-black/10 border-t-[#C8A95B] rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const dashboardRoutes = {
      admin: "/admin",
      staff: "/staff",
      client: "/client",
      driver: "/driver"
    };
    return <Navigate to={dashboardRoutes[user.role] || "/login"} replace />;
  }

  return children;
};

// Dashboard Redirect Component
const DashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-black/10 border-t-[#C8A95B] rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const dashboardRoutes = {
    admin: "/admin",
    staff: "/staff",
    client: "/client",
    driver: "/driver"
  };

  // Redirect corporate clients to corporate portal
  if (user.role === "client" && user.company_name) {
    return <Navigate to="/corporate" replace />;
  }

  return <Navigate to={dashboardRoutes[user.role] || "/login"} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/:role" element={<RoleLoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            
            {/* Guest Booking (public - no login required) */}
            <Route path="/book" element={<GuestBookingPage />} />
            
            {/* Quote Accept Page (public - no login required) */}
            <Route path="/quote/:token" element={<QuoteAcceptPage />} />
            <Route path="/quote/:token/success" element={<QuoteAcceptPage />} />
            
            {/* Public Tracking Page */}
            <Route path="/track/:bookingRef/:token" element={<TrackingPage />} />
            
            {/* Live Tracking Page (public - no login required) */}
            <Route path="/live/:bookingRef/:trackingToken" element={<LiveTrackingPage />} />
            
            {/* Public Rating Page */}
            <Route path="/rate/:bookingRef" element={<RatingPage />} />
            
            {/* Dashboard Redirect */}
            <Route path="/dashboard" element={<DashboardRedirect />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/*" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            
            {/* Staff Routes */}
            <Route path="/staff" element={
              <ProtectedRoute allowedRoles={["staff", "admin"]}>
                <StaffDashboard />
              </ProtectedRoute>
            } />
            <Route path="/staff/*" element={
              <ProtectedRoute allowedRoles={["staff", "admin"]}>
                <StaffDashboard />
              </ProtectedRoute>
            } />
            
            {/* Client Routes */}
            <Route path="/client" element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientDashboard />
              </ProtectedRoute>
            } />
            <Route path="/client/*" element={
              <ProtectedRoute allowedRoles={["client"]}>
                <ClientDashboard />
              </ProtectedRoute>
            } />
            
            {/* Corporate Portal */}
            <Route path="/corporate" element={
              <ProtectedRoute allowedRoles={["client"]}>
                <CorporatePortal />
              </ProtectedRoute>
            } />
            <Route path="/corporate/*" element={
              <ProtectedRoute allowedRoles={["client"]}>
                <CorporatePortal />
              </ProtectedRoute>
            } />
            
            {/* Driver Routes */}
            <Route path="/driver" element={
              <ProtectedRoute allowedRoles={["driver"]}>
                <DriverDashboard />
              </ProtectedRoute>
            } />
            <Route path="/driver/*" element={
              <ProtectedRoute allowedRoles={["driver"]}>
                <DriverDashboard />
              </ProtectedRoute>
            } />
            
            {/* Booking Routes */}
            <Route path="/booking/new" element={
              <ProtectedRoute allowedRoles={["admin", "staff", "client"]}>
                <BookingForm />
              </ProtectedRoute>
            } />
            <Route path="/booking/:id" element={
              <ProtectedRoute allowedRoles={["admin", "staff", "client", "driver"]}>
                <BookingDetail />
              </ProtectedRoute>
            } />
            
            {/* Settings Route (Admin only) */}
            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <SettingsPage />
              </ProtectedRoute>
            } />

            {/* Invoices */}
            <Route path="/invoices" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <InvoicesPage />
              </ProtectedRoute>
            } />

            {/* Reports */}
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <ReportsPage />
              </ProtectedRoute>
            } />

            {/* Ratings */}
            <Route path="/ratings" element={
              <ProtectedRoute allowedRoles={["admin", "staff"]}>
                <RatingsManagementPage />
              </ProtectedRoute>
            } />

            {/* Payroll (Admin only) */}
            <Route path="/payroll" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <PayrollPage />
              </ProtectedRoute>
            } />

            {/* Extras Management */}
            <Route path="/extras" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <ExtrasPage />
              </ProtectedRoute>
            } />

            {/* Bulk Booking Import */}
            <Route path="/bulk-booking" element={
              <ProtectedRoute allowedRoles={["admin", "staff"]}>
                <BulkBookingPage />
              </ProtectedRoute>
            } />

            {/* City Landing Pages - SEO */}
            <Route path="/cities" element={<CitiesPage />} />
            <Route path="/corporate-travel" element={<CorporateTravelPage />} />
            <Route path="/fleet" element={<FleetPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/services/airport-transfers" element={<AirportTransfersPage />} />
            <Route path="/services/minibus-charter" element={<MinibusCharterPage />} />
            <Route path="/services/hens-bucks" element={<HensBucksPage />} />
            <Route path="/limo-hire/:slug" element={<LocationPage />} />
            <Route path="/tours" element={<ToursPage />} />
            <Route path="/tours/:slug" element={<TourPackagePage />} />
            <Route path="/services/weddings" element={<WeddingsPage />} />
            <Route path="/services/cruise-terminal" element={<CruiseTerminalPage />} />
            <Route path="/services/special-events" element={<SpecialEventsPage />} />
            <Route path="/chauffeur/brisbane" element={<BrisbanePage />} />
            <Route path="/chauffeur/gold-coast" element={<GoldCoastPage />} />
            <Route path="/chauffeur/sydney" element={<SydneyPage />} />
            <Route path="/chauffeur/melbourne" element={<MelbournePage />} />
            <Route path="/chauffeur/cairns" element={<CairnsPage />} />
            <Route path="/chauffeur/perth" element={<PerthPage />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
